import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { toLightweightDeviation } from '../utils/toLightweightDeviation';
import Component from './components/DeviationThumbNodeView';
import { getExternalRenderer, getNodeFromSelection } from '../../components/utils/tiptap';
import { EXTENSION_TYPE_NAME } from './constants';
import { NodeSelection } from '@tiptap/pm/state';
import { Url } from '@wix/da-url';
export var MenuState = /*#__PURE__*/ function(MenuState) {
    MenuState[MenuState["HIDDEN"] = 0] = "HIDDEN";
    MenuState[MenuState["SHOW"] = 1] = "SHOW";
    return MenuState;
}({});
/**
 * Note that this wraps in <code> tag so that copy/paste works as span
 * seems to be ignored by tiptap. Not great but it works.
 */ export const DaDeviationThumb = Node.create({
    priority: 1,
    name: 'da-deviation-thumb',
    group: 'inline unlinkable',
    inline: true,
    draggable: true,
    addStorage () {
        return {
            menuState: 0
        };
    },
    addOptions () {
        return {
            defaultWidth: 150,
            canLink: true,
            canConfigure: true
        };
    },
    addAttributes () {
        return {
            deviation: {
                default: null
            },
            link: {
                default: null
            },
            width: {
                default: null
            },
            height: {
                default: null
            },
            cropping: {
                default: 'fill'
            },
            aspect: {
                default: null
            }
        };
    },
    /**
   * Used to parse incoming html, usually via paste
   */ parseHTML () {
        return [
            {
                tag: `div[data-da-type="da-deviation-thumb"]`,
                getAttrs (node) {
                    if (!node || !node.getAttribute) {
                        return false;
                    }
                    const dataType = node.getAttribute('data-da-type');
                    if (!dataType || dataType !== EXTENSION_TYPE_NAME) {
                        return false;
                    }
                    try {
                        var _node_getAttribute, _node_getAttribute1, _node_getAttribute2, _node_getAttribute3, _node_getAttribute4, _node_getAttribute5;
                        return {
                            deviation: JSON.parse((_node_getAttribute = node.getAttribute('data-deviation')) !== null && _node_getAttribute !== void 0 ? _node_getAttribute : ''),
                            link: (_node_getAttribute1 = node.getAttribute('data-link')) !== null && _node_getAttribute1 !== void 0 ? _node_getAttribute1 : undefined,
                            width: (_node_getAttribute2 = node.getAttribute('data-width')) !== null && _node_getAttribute2 !== void 0 ? _node_getAttribute2 : undefined,
                            height: (_node_getAttribute3 = node.getAttribute('data-height')) !== null && _node_getAttribute3 !== void 0 ? _node_getAttribute3 : undefined,
                            cropping: (_node_getAttribute4 = node.getAttribute('data-cropping')) !== null && _node_getAttribute4 !== void 0 ? _node_getAttribute4 : undefined,
                            aspect: (_node_getAttribute5 = node.getAttribute('data-aspect')) !== null && _node_getAttribute5 !== void 0 ? _node_getAttribute5 : undefined
                        };
                    } catch (e) {}
                    return null;
                }
            }
        ];
    },
    /**
   * Used to render HTML version of the doc, this is not used for viewer and not used
   * in edit mode either.
   *
   * Will be used for public API on the server
   *
   * This seems to be called for some reason on unmount and then everything is also
   * undefined so make sure you guard things.
   */ renderHTML ({ HTMLAttributes: { deviation, width, height, link, cropping, aspect } }) {
        try {
            const deviationMediaService = getExternalRenderer(this.editor, 'mediaService');
            const passAttribs = {
                'data-da-type': EXTENSION_TYPE_NAME,
                'data-deviation': JSON.stringify(deviation),
                'data-width': width,
                'data-height': height,
                'data-link': link,
                'data-cropping': cropping,
                'data-aspect': aspect
            };
            const thumb = deviationMediaService.getSmallestThumbThatFullyCovers(deviation, width !== null && width !== void 0 ? width : 150, height !== null && height !== void 0 ? height : Infinity, (t)=>t[t.length - 1], {
                thumbTypes: [
                    '375W',
                    '414W',
                    '750W',
                    '828W'
                ]
            });
            let thumbSpec = [];
            // Its probably text
            if (!thumb) {
                thumbSpec = [
                    'a',
                    mergeAttributes(_object_spread_props(_object_spread({}, passAttribs), {
                        href: link
                    })),
                    deviation.title
                ];
            } else {
                const img = [
                    'img',
                    mergeAttributes({
                        alt: deviation.title,
                        src: deviationMediaService.getLargestFullview(deviation).src,
                        width,
                        height
                    })
                ];
                thumbSpec = [
                    'a',
                    mergeAttributes({
                        href: link !== null && link !== void 0 ? link : deviation.url
                    }),
                    img
                ];
            }
            return [
                'code',
                mergeAttributes(_object_spread({}, passAttribs)),
                thumbSpec
            ];
        } catch (e) {
            return [
                'span'
            ];
        }
    },
    addNodeView () {
        return ReactNodeViewRenderer(Component, {
            contentDOMElementTag: 'span'
        });
    },
    onSelectionUpdate () {
        if (this.editor.isActive(EXTENSION_TYPE_NAME)) {
            this.editor.commands.daDeviationThumbOpenSettingsMenu();
        } else {
            this.editor.commands.daDeviationThumbCloseSettingsMenu();
        }
    },
    addCommands () {
        return {
            daDeviationThumbAddDeviation: (deviation)=>({ commands, dispatch })=>{
                    if (dispatch) {
                        return commands.insertContent({
                            type: this.name,
                            attrs: {
                                deviation: toLightweightDeviation(deviation)
                            }
                        });
                    }
                    return true;
                },
            daDeviationThumbOpenSettingsMenu: ()=>({ editor, chain, dispatch })=>{
                    if (dispatch) {
                        this.storage.menuState = 1;
                    }
                    return true;
                },
            daDeviationThumbCloseSettingsMenu: ()=>({ editor, chain, dispatch })=>{
                    if (dispatch) {
                        this.storage.menuState = 0;
                    }
                    return true;
                },
            daDeviationThumbLink: (href)=>({ editor, chain, dispatch, commands })=>{
                    if (!this.options.canLink) {
                        return false;
                    }
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            link: href
                        });
                    }
                    return true;
                },
            daDeviationThumbUpdateAttrs: (attrs)=>({ editor, chain, dispatch, commands, state, tr })=>{
                    if (dispatch) {
                        // Find this node and store is position
                        const nodes = getNodeFromSelection(editor);
                        let nodePos;
                        if (nodes[1]) {
                            state.doc.descendants((node, pos)=>{
                                if (node === nodes[1]) {
                                    nodePos = pos;
                                    return false; // Stop traversing once we find the first paragraph
                                }
                                return true;
                            });
                        }
                        if (nodePos) {
                            // Update the attrs
                            commands.updateAttributes(this.name, _object_spread({}, attrs));
                            // Restore the selection because for some reason inlines
                            // seem to select next after updates...
                            const selection = NodeSelection.create(tr.doc, nodePos);
                            tr.setSelection(selection);
                            dispatch(tr);
                            return true;
                        }
                    }
                    return true;
                },
            daDeviationThumbDelete: ()=>({ editor, chain, dispatch, commands, state })=>{
                    if (dispatch) {
                        const { selection } = state;
                        const node = selection.node;
                        if (node) {
                            const { tr } = state;
                            tr.deleteRange(selection.from, selection.to);
                            dispatch(tr);
                        }
                        return true;
                    }
                    return true;
                },
            daDeviationThumbRefresh: (deviation)=>({ editor, chain, dispatch, commands, state })=>{
                    if (dispatch) {
                        commands.updateAttributes(this.name, {
                            deviation: toLightweightDeviation(deviation)
                        });
                        return true;
                    }
                    return true;
                },
            daDeviationThumbReplace: (deviation)=>({ editor, chain, dispatch, commands, state })=>{
                    if (dispatch) {
                        commands.updateAttributes(this.name, {
                            deviation: toLightweightDeviation(deviation)
                        });
                        return true;
                    }
                    return true;
                },
            daDeviationThumbConvertToFullview: ()=>({ editor, chain, dispatch, commands, state })=>{
                    if (dispatch) {
                        const { selection } = state;
                        const { from, to } = selection;
                        let attributes = {};
                        state.doc.nodesBetween(from, to, (node)=>{
                            if (node.type.name === this.name) {
                                attributes = node.attrs;
                            }
                        });
                        if (attributes !== undefined) {
                            var _attributes_link;
                            return chain().daDeviationThumbDelete().daDeviationAddDeviation(attributes.deviation).daDeviationLink((_attributes_link = attributes.link) !== null && _attributes_link !== void 0 ? _attributes_link : '').run();
                        }
                        return true;
                    }
                    return true;
                },
            daDeviationThumbToLink: ()=>({ editor, chain, dispatch, commands, state })=>{
                    if (dispatch) {
                        const { selection } = state;
                        const { from, to } = selection;
                        let attributes = null;
                        state.doc.nodesBetween(from, to, (node)=>{
                            if (node.type.name === this.name) {
                                attributes = node.attrs;
                            }
                        });
                        if (attributes && attributes.deviation) {
                            const deviation = attributes.deviation;
                            let link = '';
                            if (deviation.isPublished) {
                                var _deviation_url, _ref;
                                link = (_ref = (_deviation_url = deviation.url) !== null && _deviation_url !== void 0 ? _deviation_url : deviation.shortUrl) !== null && _ref !== void 0 ? _ref : '';
                            } else {
                                var _deviation_stashPrivateid;
                                var _deviation_stashPrivateid_toString;
                                link = Url.stashItemLink((_deviation_stashPrivateid_toString = (_deviation_stashPrivateid = deviation.stashPrivateid) === null || _deviation_stashPrivateid === void 0 ? void 0 : _deviation_stashPrivateid.toString(36)) !== null && _deviation_stashPrivateid_toString !== void 0 ? _deviation_stashPrivateid_toString : '');
                            }
                            var _deviation_title;
                            chain().daDeviationDelete().insertContent({
                                type: 'text',
                                marks: [
                                    {
                                        type: 'link',
                                        attrs: {
                                            href: link
                                        }
                                    }
                                ],
                                text: (_deviation_title = deviation.title) !== null && _deviation_title !== void 0 ? _deviation_title : link
                            }).run();
                        }
                        return true;
                    }
                    return true;
                }
        };
    }
});

//# sourceMappingURL=deviation-thumb.js.map